import { Toggle } from "../shared/toggle";
import { Button } from "../shared/button";
import { Spinner } from "../shared/spinner";
import { useSettings } from "../../hooks/use-settings";
import "./settings-form.css";

interface SettingsFormProps {
  onModeChange?: (mode: 1 | 2) => void;
}

export function SettingsForm({ onModeChange }: SettingsFormProps) {
  const { settings, loading, saving, error, update } = useSettings();

  if (loading) {
    return (
      <div style={{ padding: "32px 0", textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="settings-wrap">
        <div className="card" style={{ color: "var(--red)", textAlign: "center" }}>
          {error || "Failed to load settings"}
        </div>
      </div>
    );
  }

  return (
    <div className="settings-wrap">
      {error && (
        <div
          style={{
            background: "var(--red-bg)",
            border: "1px solid var(--red-bd)",
            color: "var(--red)",
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "12px",
          }}
        >
          {error}
        </div>
      )}

      {/* Pricing */}
      <div className="card">
        <div className="clabel">Pricing</div>
        <div className="setting-row">
          <div>
            <div className="setting-label">Service Fee Rate</div>
            <div className="setting-desc">Applied to card payments</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <input
              className="num-input"
              type="number"
              min={0.1}
              max={10}
              step={0.1}
              value={settings.fee_rate}
              onChange={(e) => update({ fee_rate: parseFloat(e.target.value) || 0 })}
              disabled={saving}
            />
            <span style={{ fontWeight: 700, color: "var(--ink2)" }}>%</span>
          </div>
        </div>
        <div className="setting-row" style={{ borderBottom: "none" }}>
          <div>
            <div className="setting-label">Active Mode</div>
            <div className="setting-desc">Switch anytime</div>
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            <Button
              variant={settings.mode === 1 ? "dark" : "outline"}
              onClick={() => {
                update({ mode: 1 });
                onModeChange?.(1);
              }}
              disabled={saving}
            >
              Mode 1
            </Button>
            <Button
              variant={settings.mode === 2 ? "dark" : "outline"}
              onClick={() => {
                update({ mode: 2 });
                onModeChange?.(2);
              }}
              disabled={saving}
            >
              Mode 2
            </Button>
          </div>
        </div>
      </div>

      {/* Receipt Options */}
      <div className="card">
        <div className="clabel">Receipt Options</div>
        <div className="setting-row">
          <div>
            <div className="setting-label">Show Fee Breakdown</div>
            <div className="setting-desc">Card receipts show base + fee line</div>
          </div>
          <Toggle
            checked={settings.receipt_breakdown}
            onChange={(v) => update({ receipt_breakdown: v })}
          />
        </div>
        <div className="setting-row">
          <div>
            <div className="setting-label">Show Savings on Cash Receipt</div>
            <div className="setting-desc">How much customer saved vs card</div>
          </div>
          <Toggle
            checked={settings.receipt_savings}
            onChange={(v) => update({ receipt_savings: v })}
          />
        </div>
        <div className="setting-row">
          <div>
            <div className="setting-label">Show SCD Branding</div>
            <div className="setting-desc">Displays Simple Cash Discount on receipts</div>
          </div>
          <Toggle
            checked={settings.receipt_branding}
            onChange={(v) => update({ receipt_branding: v })}
          />
        </div>
        <div
          className="setting-row"
          style={{ borderBottom: "none", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}
        >
          <div>
            <div className="setting-label">Custom Receipt Footer</div>
            <div className="setting-desc">Printed at bottom of every receipt</div>
          </div>
          <input
            type="text"
            maxLength={120}
            value={settings.receipt_footer}
            onChange={(e) => update({ receipt_footer: e.target.value })}
            placeholder="e.g. Thank you for your business!"
            disabled={saving}
            style={{
              width: "100%",
              padding: "9px 12px",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              fontSize: "13px",
              fontFamily: "var(--font)",
              background: "var(--bg)",
            }}
          />
        </div>
      </div>

      {/* Bill Options */}
      <div className="card">
        <div className="clabel">Bill Options</div>
        <div
          style={{
            fontSize: "12px",
            color: "var(--ink2)",
            lineHeight: 1.6,
            marginBottom: "14px",
            padding: "10px",
            background: "var(--blue-bg)",
            borderRadius: "8px",
            border: "1px solid var(--blue-bd)",
          }}
        >
          Adds cash &amp; card pricing to bills printed from your Square receipt printer.
        </div>
        <div className="setting-row">
          <div>
            <div className="setting-label">Show Cash/Card Price Per Item</div>
            <div className="setting-desc">Each line item shows both prices on bill</div>
          </div>
          <Toggle
            checked={settings.bill_per_item}
            onChange={(v) => update({ bill_per_item: v })}
          />
        </div>
        <div className="setting-row" style={{ borderBottom: "none" }}>
          <div>
            <div className="setting-label">Show Cash/Card Totals on Bill</div>
            <div className="setting-desc">Bottom of bill shows both total options</div>
          </div>
          <Toggle
            checked={settings.bill_totals}
            onChange={(v) => update({ bill_totals: v })}
          />
        </div>
      </div>
    </div>
  );
}
